<script lang="ts">
    import type {TableRow} from '../kp-record-detail-table/types';
    import type {TabId} from 'shared/ui-widgets/tabset/types';
    import type ExemplarList from '../exemplar-list';
    import {getDateFormatter, getInjector, getLocalization} from 'core/svelte-context/context';
    import {pipe} from 'core/utils';
    import {DocumentDetailTabsetService} from './document-detail-tabset.service';
    import KpGenericTabGroup from 'shared/ui-widgets/tabset/KpGenericTabGroup.svelte';
    import KpGenericTabList from 'shared/ui-widgets/tabset/KpGenericTabList.svelte';
    import KpGenericTabItem from 'shared/ui-widgets/tabset/KpGenericTabItem.svelte';
    import KpGenericTabPanels from 'shared/ui-widgets/tabset/KpGenericTabPanels.svelte';
    import KpGenericTabPanelContent from 'shared/ui-widgets/tabset/KpGenericTabPanelContent.svelte';
    import KpGenericPanel from 'shared/ui-widgets/panel/KpGenericPanel.svelte';
    import KpCitation from '../kp-citation/KpCitation.svelte';
    import KpRecordDetailTable from '../kp-record-detail-table/KpRecordDetailTable.svelte';
    import KpActionRequestButton from 'shared/components/kp-action-request-button/KpActionRequestButton.svelte';
    import {actionRequestMethods} from 'shared/constants/portaro.constants';
    import KpExemplarsList from '../kp-exemplars-list/KpExemplarsList.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import KpRecordMarcTable from '../kp-record-marc-table/KpRecordMarcTable.svelte';
    import KpDocumentMini from '../kp-document-mini/KpDocumentMini.svelte';
    import KpPaginatedPanelList from 'shared/components/kp-paginated-panel-list/KpPaginatedPanelList.svelte';
    import KpRecordOperationMini from '../operation/KpRecordOperationMini.svelte';
    import KpLoanMini from '../../loan/kp-loan-mini/KpLoanMini.svelte';
    import KpTabGroupChangeListener from 'shared/ui-widgets/tabset/KpTabGroupChangeListener.svelte';
    import KpTabGroupConfiguredActiveTabSelector from 'shared/ui-widgets/tabset/KpTabGroupConfiguredActiveTabSelector.svelte';
    import KpRecordInformationTable from '../kp-record-information-table/KpRecordInformationTable.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import type {Document, DocumentTabsConfiguration, LabeledIdentified, RecordRevision, ViewableField} from 'typings/portaro.be.types';

    export let record: Document;
    export let exemplarList: ExemplarList | undefined;
    export let citationStyles: LabeledIdentified<string>[] = [];
    export let detailTableRows: TableRow[] = [];
    export let exemplarColumns: LabeledIdentified<string>[] = [];
    export let marcFields: ViewableField[];
    export let tabsConfiguration: DocumentTabsConfiguration;
    export let exemplarTabsByBuildingsEnabled: boolean;
    export let showInformationTab: boolean;

    const service = getInjector().getByClass(DocumentDetailTabsetService);

    const articlesPageableList = service.createArticlesPageableList(record);
    const partsPageableList = service.createPartsPageableList(record);
    const loanPageableList = service.createLoanPageableList(record);
    const operationPageableList = service.createOperationPageableList(record);

    let revisions: RecordRevision[] = [];
    let articleItemsExist = false;
    let partItemsExist = false;

    partsPageableList.loadFirstPage().then((parts) => partItemsExist = parts.length > 0)
    articlesPageableList.loadFirstPage().then((articles) => articleItemsExist = articles.length > 0)

    $: exemplarsVisible = tabsConfiguration.exemplars.showable && !exemplarTabsByBuildingsEnabled;
    $: citationVisible = tabsConfiguration.citation.showable;
    $: articlesVisible = articleItemsExist && tabsConfiguration.articles.showable;
    $: partsVisible = partItemsExist && tabsConfiguration.articles.showable;
    $: tocVisible = (record.tocContent || record.tocUrl) && tabsConfiguration.toc.showable;
    $: marcVisible = tabsConfiguration.marc.showable;
    $: loansVisible = tabsConfiguration.loans.showable;
    $: operationsVisible = tabsConfiguration.operations.showable;
    $: revisionsVisible = tabsConfiguration.revisions.showable;
    $: detailVisible = tabsConfiguration.detail.showable;
    $: informationVisible = showInformationTab;
    $: fullWidthTabs = showFullWidthTabs(
        exemplarsVisible, citationVisible, articlesVisible,
        partsVisible, tocVisible, marcVisible, detailVisible,
        loansVisible, operationsVisible, revisionsVisible,
        informationVisible);

    const dateFormatter = getDateFormatter();
    const localize = getLocalization();

    function showFullWidthTabs(...values: boolean[]): boolean {
        // Count shown tabs
        return values.filter((value) => value).length <= 4;
    }

    async function handleTabChange(event: CustomEvent<TabId>) {
        if (event.detail === 'tab-loans') {
            await loanPageableList.loadFirstPage()
            return;
        }

        if (event.detail === 'tab-revisions') {
            revisions = await service.loadRevisions(record);
            return;
        }

        if (event.detail === 'tab-operations') {
            await operationPageableList.loadFirstPage();
        }
    }
</script>

<KpGenericTabGroup additionalClasses="detail-blok-details">
    <KpTabGroupConfiguredActiveTabSelector config="{tabsConfiguration}"/>
    <KpTabGroupChangeListener on:tab-change={handleTabChange}/>

    <KpGenericTabList>
        {#if exemplarsVisible}
            <KpGenericTabItem fullWidth="{fullWidthTabs}" tabId="tab-exemplars">
                {#if record.periodical}
                    {localize(/* @kp-localization detail.svazaneRocniky */ 'detail.svazaneRocniky')}
                {:else}
                    {localize(/* @kp-localization detail.exemplare */ 'detail.exemplare')}
                {/if}

                {#if exemplarList}
                    <span class="text-muted">{exemplarList.filteredExemplars.length}</span>
                {/if}
            </KpGenericTabItem>
        {/if}

        {#if citationVisible}
            <KpGenericTabItem fullWidth="{fullWidthTabs}"
                              tabId="tab-citation">
                {localize(/* @kp-localization detail.citace */ 'detail.citace')}
            </KpGenericTabItem>
        {/if}

        {#if articlesVisible}
            <KpGenericTabItem fullWidth="{fullWidthTabs}"
                              tabId="tab-articles">
                {localize(/* @kp-localization detail.Souvisejici */ 'detail.Souvisejici')}
            </KpGenericTabItem>
        {/if}

        {#if partsVisible}
            <KpGenericTabItem fullWidth="{fullWidthTabs}" tabId="tab-parts">
                {localize(/* @kp-localization detail.VsechnyDily */ 'detail.VsechnyDily')}
            </KpGenericTabItem>
        {/if}

        {#if tocVisible}
            <KpGenericTabItem fullWidth="{fullWidthTabs}" tabId="tab-toc">
                {localize(/* @kp-localization detail.obsah */ 'detail.obsah')}
            </KpGenericTabItem>
        {/if}

        {#if detailVisible}
            <KpGenericTabItem fullWidth="{fullWidthTabs}" tabId="tab-detail">
                {localize(/* @kp-localization detail.detail */ 'detail.detail')}
            </KpGenericTabItem>
        {/if}

        {#if marcVisible}
            <KpGenericTabItem fullWidth="{fullWidthTabs}" tabId="tab-marc">
                {localize(/* @kp-localization detail.marc */ 'detail.marc')}
            </KpGenericTabItem>
        {/if}

        {#if loansVisible}
            <KpGenericTabItem fullWidth="{fullWidthTabs}" tabId="tab-loans">
                {localize(/* @kp-localization loan.LoanHistory */ 'loan.LoanHistory')}
            </KpGenericTabItem>
        {/if}

        {#if operationsVisible}
            <KpGenericTabItem fullWidth="{fullWidthTabs}" tabId="tab-operations">
                {localize(/* @kp-localization record.ProcessingHistory */ 'record.ProcessingHistory')}
            </KpGenericTabItem>
        {/if}

        {#if revisionsVisible}
            <KpGenericTabItem fullWidth="{fullWidthTabs}" tabId="tab-revisions">
                Předchozí verze
            </KpGenericTabItem>
        {/if}

        {#if informationVisible}
            <KpGenericTabItem fullWidth="{fullWidthTabs}" tabId="tab-information">
                {localize(/* @kp-localization record.RecordInfo */ 'record.RecordInfo')}
            </KpGenericTabItem>
        {/if}

        <KpGenericTabItem tabId="tab-none" hidden="{true}"></KpGenericTabItem>
    </KpGenericTabList>

    <KpGenericTabPanels>
        {#if exemplarsVisible}
            <KpGenericTabPanelContent tabPanelId="tab-panel-exemplars">
                <KpGenericPanel hasBodyPadding="{!exemplarList}">
                    {#if !exemplarList}
                        <KpLoadingBlock size="xs">
                            <span class="text-muted">{localize(/* @kp-localization detail.nacitamExemplare */ 'detail.nacitamExemplare')}</span>
                        </KpLoadingBlock>
                    {/if}

                    {#if exemplarList && exemplarList.empty}
                        <div class="exemplar-processing-container text-muted">
                            <IconedContent icon="info" orientation="vertical">
                                {#if record.status.id === 2 || record.status.id === 3}
                                    <span>{localize(/* @kp-localization detail.DokumentJeVeZpracovani */ 'detail.DokumentJeVeZpracovani')}</span>
                                {/if}

                                {#if record.status.id !== 2 && record.status.id !== 3}
                                    <span>
                                        {#if record.periodical}
                                            <span>{localize(/* @kp-localization detail.dokumentNemaZadneSvazaneRocniky */ 'detail.dokumentNemaZadneSvazaneRocniky')}</span>
                                        {:else}
                                             <span>{localize(/* @kp-localization detail.ZadneExemplareKDispozici */ 'detail.ZadneExemplareKDispozici')}</span>
                                        {/if}
                                    </span>
                                {/if}
                            </IconedContent>
                        </div>
                    {/if}

                    {#if exemplarList && !exemplarList.empty}
                        <KpExemplarsList exemplars="{exemplarList.filteredExemplars}"
                                         document="{record}"
                                         properties="{exemplarColumns}"
                                         exemplarFilter="{exemplarList.exemplarFilter}"/>
                    {/if}

                    {#if exemplarList && exemplarList.canCreateAny()}
                        <div class="add-exemplar-button-container">
                            <KpButton on:click={() => service.createExemplar(record)}>
                                <IconedContent icon="add">
                                    {localize(/* @kp-localization commons.pridatExemplar */ 'commons.pridatExemplar')}
                                </IconedContent>
                            </KpButton>
                        </div>
                    {/if}
                </KpGenericPanel>
            </KpGenericTabPanelContent>
        {/if}

        {#if citationVisible}
            <KpGenericTabPanelContent tabPanelId="tab-panel-citation">
                <KpGenericPanel>
                    <KpCitation record="{record}" availableStyles="{citationStyles}"/>
                </KpGenericPanel>
            </KpGenericTabPanelContent>
        {/if}

        {#if articlesVisible}
            <KpGenericTabPanelContent tabPanelId="tab-panel-articles">
                <KpGenericPanel>
                    <KpPaginatedPanelList pageableList="{articlesPageableList}"
                                          isInPaddedPanel="{true}"
                                          let:item>
                        <KpDocumentMini document="{item}"/>
                    </KpPaginatedPanelList>
                </KpGenericPanel>
            </KpGenericTabPanelContent>
        {/if}

        {#if partsVisible}
            <KpGenericTabPanelContent tabPanelId="tab-panel-parts">
                <KpGenericPanel>
                    <KpPaginatedPanelList pageableList="{partsPageableList}"
                                          isInPaddedPanel="{true}"
                                          let:item>
                        <KpDocumentMini document="{item}"/>
                    </KpPaginatedPanelList>
                </KpGenericPanel>
            </KpGenericTabPanelContent>
        {/if}

        {#if tocVisible}
            <KpGenericTabPanelContent tabPanelId="tab-panel-toc">
                <KpGenericPanel>
                    {#if record.tocUrl}
                        <div class="toc-url">
                            <a href="{record.tocUrl}">{record.tocUrl}</a>
                        </div>
                    {/if}

                    {#if record.tocContent}
                        <div class="toc-content">{record.tocContent}</div>
                    {/if}
                </KpGenericPanel>
            </KpGenericTabPanelContent>
        {/if}

        {#if detailVisible}
            <KpGenericTabPanelContent tabPanelId="tab-panel-detail">
                <KpGenericPanel hasBodyPadding="{false}">
                    <KpRecordDetailTable rows="{detailTableRows}"/>
                </KpGenericPanel>
            </KpGenericTabPanelContent>
        {/if}

        {#if marcVisible}
            <KpGenericTabPanelContent tabPanelId="tab-panel-marc">
                <KpGenericPanel hasBodyPadding="{false}">
                    <KpRecordMarcTable fields="{marcFields}"/>
                </KpGenericPanel>
            </KpGenericTabPanelContent>
        {/if}

        {#if loansVisible}
            <KpGenericTabPanelContent tabPanelId="tab-panel-loans">
                <KpGenericPanel>
                    <KpPaginatedPanelList pageableList="{loanPageableList}"
                                          isInPaddedPanel="{true}"
                                          let:item>
                        <KpLoanMini loan="{item}"/>
                    </KpPaginatedPanelList>
                </KpGenericPanel>
            </KpGenericTabPanelContent>
        {/if}

        {#if operationsVisible}
            <KpGenericTabPanelContent tabPanelId="tab-panel-operations">
                <KpGenericPanel>
                    <KpPaginatedPanelList pageableList="{operationPageableList}"
                                          isInPaddedPanel="{true}"
                                          let:item>
                        <KpRecordOperationMini operation="{item}"/>
                    </KpPaginatedPanelList>
                </KpGenericPanel>
            </KpGenericTabPanelContent>
        {/if}

        {#if revisionsVisible}
            <KpGenericTabPanelContent tabPanelId="tab-panel-revisions">
                <KpGenericPanel hasBodyPadding="{!revisions || revisions.length === 0}"
                                hasBorder="{!revisions || revisions.length === 0}">
                    {#if revisions && revisions.length > 0}
                        <ul class="list-group revisions-list">
                            {#each revisions as revision}
                                <li class="list-group-item">
                                    <div class="revision-item-inner">
                                        <div>{pipe(revision.date, dateFormatter('d.M.yyyy HH:mm'))}</div>

                                        <KpActionRequestButton path="/api/record/import"
                                                               requestMethod="{actionRequestMethods.POST}"
                                                               requestBody="{{record: record.id, format: 'marc21.lenient', content: revision.content}}"
                                                               buttonSize="xs">
                                            Aplikovat
                                        </KpActionRequestButton>
                                    </div>

                                    <pre>{revision.content}</pre>
                                </li>
                            {/each}
                        </ul>
                    {/if}

                    {#if !revisions || revisions.length === 0}
                        <div role="status" class="text-muted text-center">
                            {localize(/* @kp-localization commons.ZadnePolozky */ 'commons.ZadnePolozky')}
                        </div>
                    {/if}
                </KpGenericPanel>
            </KpGenericTabPanelContent>
        {/if}

        {#if operationsVisible}
            <KpGenericTabPanelContent tabPanelId="tab-panel-information">
                <KpGenericPanel hasBodyPadding="{false}">
                    <KpRecordInformationTable record="{record}"/>
                </KpGenericPanel>
            </KpGenericTabPanelContent>
        {/if}

        <KpGenericTabPanelContent tabPanelId="tab-panel-none" hidden="{true}"></KpGenericTabPanelContent>
    </KpGenericTabPanels>
</KpGenericTabGroup>

<style lang="less">
    @import (reference) "bootstrap-less/bootstrap/variables";

    .exemplar-processing-container {
        padding: @panel-body-padding;
        gap: @padding-base-vertical;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }

    .add-exemplar-button-container {
        border-top: 1px solid @panel-default-border;
        padding: @panel-body-padding;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .toc-content {
        font-size: 13px;
        overflow-x: scroll;
        white-space: pre;
        font-family: monospace;
    }

    .revisions-list {
        margin: 0;

        .revision-item-inner {
            display: flex;
            justify-content: space-between;
            margin-bottom: @padding-base-vertical;
        }
    }
</style>